/** @format **/
import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import SimCardInterface from '../../../interfaces/platform/SimCardInterface';
import CardHeader from '../../../components/Heading/CardHeaderExt';
import SimCard from '@material-ui/icons/SimCard';
import CardBody from '../../../components/Card/CardBody';
import Card from '../../../components/Card/Card';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { SimCardServiceTemplateCollection } from '../../../interfaces/platform/SimCardServiceTemplate';
import { useDispatch } from 'react-redux';
import { SimpleSelect, SimpleSelectElements } from '../../../components/Adescom/SimpleSelect';
import { Typography } from '@material-ui/core';
import I18n from '../../../components/Translate/I18n';
import { DateHelperTs } from '../../../helpers/DateHelperTs';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import { AlertDialog } from '../../lib/dialog/AlertDialog';
import { ProductActions } from '../../../modules/simcard/ProductReducer';
import { roles, UserRoleHelper } from '../../../helpers/UserRoleHelper';

type ProductChangeVirtualComponentType = {
    platformResellerId: number;
    simCard: SimCardInterface;
    reload: () => void;
};

export const ProductChangeVirtualComponent: FunctionComponent<ProductChangeVirtualComponentType> = ({
    simCard,
    platformResellerId,
    reload,
}): ReactElement => {
    const dispatch = useDispatch();
    const [newSimCardTemplateId, setNewSimCardTemplateId] = useState<number | null>();
    const [newSimCardTemplateName, setNewSimCardTemplateName] = useState('');
    const [virtualComponentTemplates, setVirtualComponentTemplates] = useState<Array<SimpleSelectElements>>([]);
    const [currentName, setCurrentName] = useState('');
    const [alertDialog, setAlertDialog] = useState(false);

    useEffect(() => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/sim_card_service_templates')),
            'sim_card_services_types_list_load_activate_sim_card',
            [
                { name: 'platformResellerId', value: platformResellerId },
                { name: 'systemRestrictedFlag', value: false },
                { name: 'clip', value: true },
                { name: 'ussdSmsCommunicationId', value: -1 },
                { name: 'active', value: true },
            ],
            [{ name: 'description', value: 'asc' }],
            (data: SimCardServiceTemplateCollection) => {
                const header = [];
                for (let i = 0; i < data['hydra:member'].length; i++) {
                    if (simCard.servicesTemplateId !== data['hydra:member'][i].id) {
                        header.push({ name: data['hydra:member'][i].templateName, value: data['hydra:member'][i].id });
                    } else {
                        setCurrentName(data['hydra:member'][i].templateName);
                    }
                    if (simCard.newServicesTemplateId === data['hydra:member'][i].id) {
                        setNewSimCardTemplateName(data['hydra:member'][i].templateName);
                    }
                }
                setVirtualComponentTemplates(header);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    }, [dispatch, platformResellerId, simCard]);

    const changeSelect = (event: React.ChangeEvent<HTMLSelectElement>): void => setNewSimCardTemplateId(parseInt(event.target.value));

    const activateVirtualComponentRequest = (): void => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.putWithCallback(
            FormGeneralHelper.getUrl('/operation_on_sim_cards/' + simCard.id),
            ProductActions.PRODUCT_PUT_CHANGE_VC,
            { id: simCard.id, activationType: 6, newServicesTemplateId: newSimCardTemplateId },
            (): void => {
                setAlertDialog(false);
                setNewSimCardTemplateId(null);
                reload();
            },
            { dispatchAfterCallback: false, propagateDataToCallback: true }
        );
    };

    const disableActivateVirtualComponentRequest = (): void => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.putWithCallback(
            FormGeneralHelper.getUrl('/operation_on_sim_cards/' + simCard.id),
            ProductActions.PRODUCT_PUT_CHANGE_VC,
            { id: simCard.id, activationType: 6, newServicesTemplateId: -1 },
            (): void => {
                reload();
            },
            { dispatchAfterCallback: false, propagateDataToCallback: true }
        );
    };

    return (
        <>
            <Card>
                <CardHeader icon={<SimCard />} trans="simcard:edit.change_virtual_component" />
                <CardBody>
                    {!UserRoleHelper.checkRole(roles.ROLE_SUBSCRIPTION_VIEW_ONLY) && (
                        <SimpleSelect
                            label="simcard:edit.allowed_virtual_component"
                            required={false}
                            elements={virtualComponentTemplates}
                            dispatchName={'no-name'}
                            fieldName="discountSelect"
                            callback={changeSelect}
                            selectedValue={newSimCardTemplateId}
                        />
                    )}
                    {newSimCardTemplateId && (
                        <CustomTextButtom
                            callback={(): void => setAlertDialog(true)}
                            callbackParam={{}}
                            label="simcard:edit.call_activate_virtual_component"
                            size="sm"
                        />
                    )}
                    <Typography variant="subtitle2">
                        <I18n trans="simcard:edit.actual_virtual_component" valueObject={{ package_name: currentName }} />
                    </Typography>
                    <Typography variant="subtitle2">
                        <I18n
                            trans="simcard:edit.actual_activate_date_virtual_component"
                            valueObject={{
                                activation_date: simCard.servicesTemplateActivationDate
                                    ? DateHelperTs.format(simCard.servicesTemplateActivationDate, 'YYYY-MM-DD')
                                    : null,
                            }}
                        />
                    </Typography>
                    {simCard.newServicesTemplateId !== -1 && (
                        <>
                            <hr />
                            <Typography variant="subtitle2">
                                <I18n trans="simcard:edit.planned_virtual_component" valueObject={{ package_name: newSimCardTemplateName }} />
                            </Typography>
                            {simCard.newServicesTemplateActivationDate && (
                                <Typography variant="subtitle2">
                                    <I18n
                                        trans="simcard:edit.planned_activate_date_virtual_component"
                                        valueObject={{
                                            activation_date: simCard.servicesTemplateActivationDate
                                                ? DateHelperTs.format(simCard.newServicesTemplateActivationDate, 'YYYY-MM-DD')
                                                : null,
                                        }}
                                    />
                                </Typography>
                            )}
                            {!UserRoleHelper.checkRole(roles.ROLE_SUBSCRIPTION_VIEW_ONLY) && (
                                <CustomTextButtom
                                    callback={(): void => disableActivateVirtualComponentRequest()}
                                    callbackParam={{}}
                                    label="simcard:edit.disable_activate_virtual_component"
                                    size="sm"
                                />
                            )}
                        </>
                    )}
                    <AlertDialog
                        open={alertDialog}
                        titleDialog="simcard:edit.call_activate_virtual_component"
                        displayMessage="simcard:edit.confirm_activate_virtual_component"
                        closeCallback={(): void => {
                            setNewSimCardTemplateId(null);
                            setAlertDialog(false);
                        }}
                        confirmCallback={activateVirtualComponentRequest}
                    />
                </CardBody>
            </Card>
        </>
    );
};
